import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { SENDBIRD_NOTIFICATION_CLICK } from "utils/chatNotificationEvents";

export const MESSAGING_SW_PATH = "/firebase-messaging-sw.js";
const NOTIFICATION_ICON = "/favicon.png";
const permissionListeners = new Set();

/**
 * @returns {NotificationPermission|""}
 */
export function getNotificationPermission() {
  if (typeof Notification === "undefined") {
    return "";
  }
  return Notification.permission;
}

/**
 * @param {(permission: NotificationPermission|"") => void} listener
 * @returns {() => void}
 */
export function subscribeNotificationPermission(listener) {
  permissionListeners.add(listener);
  return () => {
    permissionListeners.delete(listener);
  };
}

function emitNotificationPermission(permission) {
  permissionListeners.forEach((listener) => {
    try {
      listener(permission);
    } catch (error) {
      console.error("Notification permission listener failed:", error);
    }
  });
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @returns {boolean}
 */
function isMessagingServiceWorker(registration) {
  const scripts = [
    registration?.active?.scriptURL,
    registration?.waiting?.scriptURL,
    registration?.installing?.scriptURL,
  ];
  return scripts.some((url) => url?.includes("firebase-messaging-sw.js"));
}

/**
 * @param {ServiceWorkerRegistration} registration
 * @returns {Promise<ServiceWorkerRegistration>}
 */
async function waitForMessagingServiceWorker(registration) {
  const worker =
    registration.active || registration.waiting || registration.installing;
  if (!worker || worker.state === "activated") {
    return registration;
  }

  if (registration.waiting) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }

  await Promise.race([
    new Promise((resolve) => {
      const onChange = () => {
        if (worker.state === "activated" || worker.state === "redundant") {
          worker.removeEventListener("statechange", onChange);
          resolve();
        }
      };
      worker.addEventListener("statechange", onChange);
    }),
    new Promise((resolve) => {
      setTimeout(resolve, 8000);
    }),
  ]);
  return registration;
}

/**
 * Registers the Firebase messaging service worker (or returns the existing one).
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerMessagingServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const existing = registrations.find(isMessagingServiceWorker);
    if (existing) {
      return waitForMessagingServiceWorker(existing);
    }

    const registration = await navigator.serviceWorker.register(
      MESSAGING_SW_PATH,
      { scope: "/" }
    );
    return waitForMessagingServiceWorker(registration);
  } catch (error) {
    console.error(
      "Failed to register Firebase messaging service worker:",
      error
    );
    return null;
  }
}

/**
 * Requests browser notification permission.
 * Firefox ignores this unless it runs from a user gesture (click/keypress).
 *
 * @returns {Promise<NotificationPermission|"">}
 */
export async function requestNotificationPermission() {
  if (typeof Notification === "undefined") {
    return "";
  }
  if (Notification.permission === "granted") {
    emitNotificationPermission("granted");
    return "granted";
  }
  if (Notification.permission === "denied") {
    emitNotificationPermission("denied");
    return "denied";
  }
  try {
    const permission = await Notification.requestPermission();
    emitNotificationPermission(permission);
    return permission;
  } catch (error) {
    console.warn("Notification permission request failed:", error);
    emitNotificationPermission(Notification.permission);
    return Notification.permission;
  }
}

/**
 * Builds the in-app path a chat notification should open.
 *
 * @param {Record<string, unknown>|null|undefined} sendbird
 * @returns {string}
 */
export function buildChatNotificationPath(sendbird) {
  const channel = sendbird?.channel;
  const channelUrl =
    channel?.channel_url || channel?.channelUrl || channel?.url || "";
  const customType = channel?.custom_type || channel?.customType || "";
  if (!channelUrl) {
    return "/";
  }

  const chat = encodeURIComponent(channelUrl);
  if (
    customType === "PATIENT_MESSENGER_OPEN_CHAT" ||
    customType === "PATIENT_MESSENGER_CLOSE_CHAT"
  ) {
    return `/conversation-redirection?chat=${chat}`;
  }
  if (customType === "external") {
    return `/team-conversation?chat=${chat}&tab=2`;
  }
  if (customType === "internal") {
    return `/team-conversation?chat=${chat}&tab=1`;
  }
  return `/team-conversation?chat=${chat}`;
}

/**
 * Normalizes an FCM payload into title, body, and click data.
 * Handles Sendbird `data.sendbird` as well as standard notification/data payloads.
 *
 * @param {import("firebase/messaging").MessagePayload|Record<string, unknown>} payload
 * @returns {{ title: string, body: string, tag: string, data: Record<string, unknown> }}
 */
export function parseFcmNotificationPayload(payload) {
  const data = payload?.data || {};
  let sendbird = null;

  if (data.sendbird) {
    try {
      sendbird =
        typeof data.sendbird === "string"
          ? JSON.parse(data.sendbird)
          : data.sendbird;
    } catch (error) {
      console.error("Failed to parse Sendbird FCM payload:", error);
    }
  }

  const title =
    sendbird?.channel?.name ||
    sendbird?.sender?.name ||
    payload?.notification?.title ||
    data.push_title ||
    data.title ||
    data.Title ||
    "Notification";

  const body =
    sendbird?.message ||
    data.message ||
    payload?.notification?.body ||
    data.body ||
    data.Body ||
    "";

  const path = buildChatNotificationPath(sendbird);
  const tag =
    sendbird?.channel?.channel_url ||
    sendbird?.channel?.url ||
    data.tag ||
    "";

  return {
    title,
    body,
    tag,
    data: { body: sendbird || data, payload, path },
  };
}

/**
 * Shows an OS desktop notification.
 * Uses the page Notification API while a tab is visible (Chrome suppresses
 * service-worker toasts for a focused client). Uses the SW when the tab is hidden.
 *
 * @param {import("firebase/messaging").MessagePayload|Record<string, unknown>} payload
 * @returns {Promise<void>}
 */
export async function showDesktopNotification(payload) {
  if (typeof Notification === "undefined") {
    return;
  }
  if (Notification.permission !== "granted") {
    return;
  }

  const { title, body, tag, data } = parseFcmNotificationPayload(payload);
  if (!title && !body) {
    return;
  }

  const options = {
    body,
    data,
    icon: NOTIFICATION_ICON,
    tag: tag || undefined,
    renotify: Boolean(tag),
  };

  const attachClick = (notification) => {
    if (!notification) return;
    notification.onclick = () => {
      window.focus();
      notification.close();
      const path = data?.path;
      if (typeof path === "string" && path.startsWith("/") && path !== "/") {
        window.dispatchEvent(
          new CustomEvent(SENDBIRD_NOTIFICATION_CLICK, { detail: { path } })
        );
      }
    };
  };

  const pageIsVisible =
    typeof document !== "undefined" && document.visibilityState === "visible";

  if (pageIsVisible) {
    try {
      attachClick(new Notification(title || "Notification", options));
      return;
    } catch (error) {
      console.error("Failed to show page notification:", error);
    }
  }

  try {
    const registration = await registerMessagingServiceWorker();
    if (registration?.showNotification) {
      await registration.showNotification(title || "Notification", options);
      return;
    }
  } catch (error) {
    console.error("Failed to show service-worker notification:", error);
  }

  try {
    attachClick(new Notification(title || "Notification", options));
  } catch (error) {
    console.error("Failed to show desktop notification:", error);
  }
}

/**
 * Requests permission, waits for the messaging SW, then returns an FCM token.
 *
 * @param {import("firebase/app").FirebaseApp} app
 * @returns {Promise<string>} Token string, or empty string when unavailable
 */
export async function getFcmTokenForApp(app) {
  const permission = await requestNotificationPermission();
  if (permission !== "granted") {
    console.warn("Notification permission not granted:", permission);
    return "";
  }

  const supported = await isSupported();
  if (
    !supported ||
    !("serviceWorker" in navigator) ||
    typeof Notification === "undefined"
  ) {
    return "";
  }

  const registration = await registerMessagingServiceWorker();
  if (!registration) {
    console.error("Firebase messaging service worker is not registered");
    return "";
  }

  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: process.env.REACT_APP_FIREBASE_PUBLIC_KEY,
    serviceWorkerRegistration: registration,
  });

  return token || "";
}
